/**
 * Admin "sign in as" (impersonation) client.
 *
 * Design rules (mirrors backend core/lib/impersonation.py):
 * - The server is the source of truth: /api/my-profile/ returns
 *   user_info.impersonation while a session is impersonated. The
 *   sessionStorage hint below is only a paint optimisation for the banner.
 * - The admin's own token is NEVER stored client-side; on exit the backend
 *   restores it from the token's parent_token_key.
 * - Both transitions end in window.location.replace() (never a router
 *   navigate): app state (userInfo, workspace, plan) is loaded on mount and
 *   an in-place navigation would keep the admin's state while acting as the
 *   customer.
 */
import UseAPI from './api/UseAPI';
import api_config from './api/common';

const API_BASE = api_config.BASE_BACKEND_ENDPOINT;

export const HINT_KEY = 'sayify-impersonation';

export const readImpersonationHint = () => {
    try {
        const raw = sessionStorage.getItem(HINT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

export const writeImpersonationHint = (payload) => {
    try {
        sessionStorage.setItem(HINT_KEY, JSON.stringify(payload || {}));
    } catch (e) { /* ignore */ }
};

export const clearImpersonationHint = () => {
    try {
        sessionStorage.removeItem(HINT_KEY);
    } catch (e) { /* ignore */ }
};

export const isImpersonating = () => Boolean(readImpersonationHint());

/**
 * Enter an impersonated session for a user.
 * @param {Object} params - {user_id or email, note (min 5 chars)}
 * @returns result object; on success the page is replaced with "/".
 */
export const enterImpersonation = async (params) => {
    const url = `${API_BASE}api/admin/impersonate/`;
    const result = await UseAPI(null, url, 'POST', params);
    if (result && result.success && result.token) {
        adoptImpersonatedSession(result);
        window.location.replace('/');
    }
    return result;
};

/**
 * Exit the impersonated session, restoring the admin's own session when the
 * backend still has it, otherwise bouncing to /login.
 */
export const exitImpersonation = async (returnTo = '/admin/users') => {
    const url = `${API_BASE}api/admin/impersonate/exit/`;
    let result = null;
    try {
        result = await UseAPI(null, url, 'POST', {});
    } catch (e) {
        result = null;
    }
    clearImpersonationHint();

    if (result && result.success && !result.relogin_required && result.token) {
        try {
            localStorage.setItem('user-token', result.token);
            localStorage.setItem('loggedIn', 'true');
            localStorage.removeItem('current_workspace_uuid');
        } catch (e) { /* ignore */ }
        window.location.replace(returnTo);
        return result;
    }

    // Admin session is gone (expired or revoked) — a clean relogin is the
    // only safe state.
    try {
        localStorage.clear();
    } catch (e) { /* ignore */ }
    window.location.replace('/login');
    return result;
};

/**
 * Swap client storage over to the impersonated user's session.
 * Keeps `loggedIn` set throughout: ProtectedRoute checks only that key and
 * would bounce to /login mid-swap otherwise.
 */
const adoptImpersonatedSession = (result) => {
    try {
        localStorage.setItem('user-token', result.token);
        localStorage.setItem('loggedIn', 'true');
        // The customer's workspace context, not the admin's.
        localStorage.removeItem('current_workspace_uuid');
        // Owner-only debug flag must never leak into a customer session.
        localStorage.removeItem('k_');
    } catch (e) { /* ignore */ }
    writeImpersonationHint({
        active: true,
        admin_email: (result.impersonation && result.impersonation.admin_email) || '',
        admin_name: (result.impersonation && result.impersonation.admin_name) || '',
        expires_at: (result.impersonation && result.impersonation.expires_at) || null,
        target_email: (result.user_info && result.user_info.email) || '',
    });
};

export default {
    readImpersonationHint,
    writeImpersonationHint,
    clearImpersonationHint,
    isImpersonating,
    enterImpersonation,
    exitImpersonation,
};
